import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import React, { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Drawing from '/@/components/drawing';
import NewNitrogenRecoCard from '/@/components/new-nitrogen-reco-card';
import { NigrogenListHeader } from '/@/components/nitrogen-smart/headers';
import Popover from '/@/components/popover';
import Spinner from '/@/components/spinner';
import colors from '/@/components/theme-provider/colors';
import Title from '/@/components/title';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { useNSRecommendations } from '/@/hooks/nitrogen-smart';
import { _ } from '/@/locale';
import { getLibraryEnabled } from '/@/store/library/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import useIsMobile from '/@/utils/hooks/is-mobile';
import useWindowSize from '/@/utils/hooks/window-size';
import { reverse } from '/@/utils/router';

const NitrogenRecommendationList = () => {
    const { data: nsRecommendations } = useNSRecommendations();
    const history = useNavigate();
    const windowWidth = useWindowSize().width;
    const [{ open, anchorEl }, { handleClick, handleClose }] = useAnchorEl();
    const isMobile = useIsMobile();

    const createNewRecommendation = () => {
        mpTrackEvent('N-Smart Recommendation began', {
            entry_source: 'nitrogen-recommendation-list',
        });
        history(reverse('nitrogenRecommendation.NitrogenRecommendationCreate'));
    };

    const libraryEnabled = useSelector(getLibraryEnabled);

    const redirect = (slug) => {
        window.open(reverse('library.LibraryDetail', { params: { slug } }));
    };

    const cropMonitoringId = new URLSearchParams(window.location.search).get('cropMonitoringId');
    const nitrogenRecommendations = useMemo(() => {
        if (!nsRecommendations) return [];
        if (cropMonitoringId)
            return nsRecommendations.filter(({ cropMonitoring }) => cropMonitoring === Number(cropMonitoringId));
        return nsRecommendations;
    }, [cropMonitoringId, nsRecommendations]);

    return (
        <React.Fragment>
            <NigrogenListHeader onNewRecomendation={createNewRecommendation} hideButtons={isMobile} />
            {nsRecommendations ? (
                <Grid container direction='column' sx={{}}>
                    {nitrogenRecommendations.length > 0 ? (
                        <Grid
                            item
                            container
                            justifyContent='center'
                            sx={{ backgroundColor: colors.gray[200], py: 2, height: '100%' }}
                        >
                            <Grid item sx={{ maxWidth: 1200, width: '100%', backgroundColor: colors.gray[200], py: 2 }}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { md: '1fr 1fr', lg: '1fr 1fr 1fr' },
                                        gap: 4,
                                        px: 4,
                                    }}
                                >
                                    <Grid item sx={{ display: { xs: 'block', md: 'none' } }}>
                                        <Button fullWidth onClick={createNewRecommendation} variant='contained'>
                                            {_('+ Nueva Recomendación')}
                                        </Button>
                                    </Grid>
                                    {nitrogenRecommendations.map((nitrogenRecommendation) => {
                                        return <NewNitrogenRecoCard recommendation={nitrogenRecommendation} />;
                                    })}
                                </Box>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid item container sx={{ margin: { md: 'auto' } }} direction='column'>
                            {isMobile && (
                                <Grid item sx={{ p: 2 }}>
                                    <Button fullWidth onClick={createNewRecommendation} variant='contained'>
                                        {_('+ Nueva Recomendación')}
                                    </Button>
                                </Grid>
                            )}
                            <Grid item sx={{ py: 16 }}>
                                <Drawing
                                    type='crop-monitoring-not-found'
                                    title={
                                        <Fragment>
                                            <Title size='md' bolder color='gray-light'>
                                                {_('¡Aún no tenés ninguna Recomendación N-Smart! ')}
                                            </Title>
                                            <Title size='md' color='gray-light'>
                                                {_('Para crear una, hacé click en ')}
                                                <span
                                                    style={{
                                                        color: (t) => t.palette.primary.main,
                                                        fontStyle: 'italic',
                                                        textDecoration: 'underline',
                                                        fontWeight: 800,
                                                    }}
                                                >
                                                    {_('+ Nueva Recomendación')}
                                                </span>
                                            </Title>
                                        </Fragment>
                                    }
                                />
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            ) : (
                <Box width='100%' height='100%' display='flex'>
                    <Box mx='auto' my='auto'>
                        <Spinner />
                    </Box>
                </Box>
            )}

            {cropMonitoringId && (
                <Box sx={{ backgroundColor: colors.gray[200], px: 2, display: 'flex', placeContent: 'center' }}>
                    <Box sx={{ p: 2, maxWidth: 1200, width: '100%' }}>
                        <Button
                            fullWidth
                            onClick={() => history(reverse('nitrogenRecommendation.NitrogenRecommendationList'))}
                            variant='contained'
                        >
                            {_('Ver todos')}
                        </Button>
                    </Box>
                </Box>
            )}
            {isMobile && libraryEnabled && (
                <Grid
                    item
                    sx={{
                        position: 'fixed',
                        bottom: 30,
                        right: 30,
                        borderRadius: '50%',
                        height: 40,
                        width: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: (t) => t.palette.accent.main,
                        boxShadow: (t) => `${t.palette.accent.main} 0px 8px 24px`,
                    }}
                    onClick={handleClick}
                >
                    <div style={{ fontWeight: 800, color: 'white', fontSize: 24 }}>?</div>
                </Grid>
            )}
            <Popover open={open && isMobile} handleClose={handleClose} title={_('Quiero saber más')}>
                <Grid item container direction='column' sx={{ gap: 1 }}>
                    <Grid item>
                        <Button variant='contained' onClick={() => redirect('nitrogeno-smart-nutrientes')} fullWidth>
                            {_('Sobre nutrientes')}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant='outlined' onClick={() => redirect('nitrogeno-smart-programa')} fullWidth>
                            {_('Sobre el programa')}
                        </Button>
                    </Grid>
                </Grid>
            </Popover>
        </React.Fragment>
    );
};

export default NitrogenRecommendationList;
